import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const ChatOverlay = ({ isOpen, onClose }) => {
    // Load persisted chat data on mount
    const [messages, setMessages] = useState(() => {
        try {
            const saved = sessionStorage.getItem('paradox_chat_messages');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState(() => {
        try {
            return sessionStorage.getItem('paradox_conversation_id') || null;
        } catch (e) {
            return null;
        }
    });
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [showAdvancedLoading, setShowAdvancedLoading] = useState(false);
    const messagesEndRef = useRef(null);

    // Loading messages for research phases (after 5 seconds)
    const advancedLoadingMessages = [
        "Conducting deep research...",
        "Analyzing patterns...",
        "Synthesizing insights...",
        "Processing data streams...",
        "Generating intelligent response..."
    ];

    // ESC key handler
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                event.preventDefault();
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
        // Save messages to sessionStorage whenever they change
        try {
            sessionStorage.setItem('paradox_chat_messages', JSON.stringify(messages));
        } catch (e) {
            console.warn('Failed to save chat messages:', e);
        }
    }, [messages]);

    // Save conversation ID when it changes
    useEffect(() => {
        if (conversationId) {
            try {
                sessionStorage.setItem('paradox_conversation_id', conversationId);
            } catch (e) {
                console.warn('Failed to save conversation ID:', e);
            }
        }
    }, [conversationId]);

    // Handle loading states - show "Loading..." for first 5 seconds, then advanced messages
    useEffect(() => {
        if (isLoading) {
            setShowAdvancedLoading(false);
            setLoadingMessageIndex(0);
            
            // After 5 seconds, switch to advanced loading messages
            const advancedTimeout = setTimeout(() => {
                setShowAdvancedLoading(true);
            }, 7000);
            
            // Start cycling through advanced messages after 5 seconds
            const interval = setTimeout(() => {
                const cycleInterval = setInterval(() => {
                    setLoadingMessageIndex(prev => (prev + 1) % advancedLoadingMessages.length);
                }, 2500); // Change message every 2.5 seconds
                
                // Clean up cycle interval when loading stops
                return () => clearInterval(cycleInterval);
            }, 7000);
            
            return () => {
                clearTimeout(advancedTimeout);
                clearTimeout(interval);
            };
        } else {
            setShowAdvancedLoading(false);
            setLoadingMessageIndex(0);
        }
    }, [isLoading, advancedLoadingMessages.length]);

    const sendMessage = async (message) => {
        if (!message.trim()) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: message,
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            const response = await fetch('/chat/send', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': window.csrfToken || '',
                },
                credentials: 'include',
                body: JSON.stringify({
                    message: message,
                    conversation_id: conversationId,
                    history: messages.slice(-10)
                })
            });

            const data = await response.json();

            if (data.success) {
                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: data.response,
                    timestamp: data.timestamp
                };

                setMessages(prev => [...prev, aiMessage]);

                if (data.conversation_id && !conversationId) {
                    setConversationId(data.conversation_id);
                }
            } else {
                throw new Error(data.error || 'Failed to get AI response');
            }
        } catch (error) {
            console.error('Chat error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date().toISOString(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const clearChat = () => {
        setMessages([]);
        setConversationId(null);
        try {
            sessionStorage.removeItem('paradox_chat_messages');
            sessionStorage.removeItem('paradox_conversation_id');
        } catch (e) {
            console.warn('Failed to clear chat storage:', e);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(inputMessage);
    };

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderStructuredContent = (content) => {
        // Split content into paragraphs and handle different formatting
        const paragraphs = content.split('\n').filter(p => p.trim());
        
        return (
            <div className="structured-content">
                {paragraphs.map((paragraph, index) => {
                    const trimmed = paragraph.trim();
                    
                    // Check for stage headers (all caps)
                    if (trimmed.match(/^\*\*STAGE \d+:/)) {
                        return (
                            <div key={index} className="stage-header">
                                {trimmed.replace(/\*\*/g, '')}
                            </div>
                        );
                    }
                    
                    // Check for section headers (all caps with **:)
                    if (trimmed.match(/^\*\*[A-Z\s]+:\*\*/)) {
                        return (
                            <div key={index} className="section-header">
                                {trimmed.replace(/\*\*/g, '')}
                            </div>
                        );
                    }
                    
                    // Check for bullet points (starting with -)
                    if (trimmed.startsWith('- ')) {
                        return (
                            <div key={index} className="bullet-point">
                                {trimmed.substring(2)}
                            </div>
                        );
                    }
                    
                    // Check for subsection headers (contains :)
                    if (trimmed.includes(':') && !trimmed.startsWith('- ') && trimmed.length < 100) {
                        const [label, ...rest] = trimmed.split(':');
                        if (rest.length > 0) {
                            return (
                                <div key={index} className="subsection">
                                    <span className="subsection-label">{label}:</span>
                                    <span className="subsection-content">{rest.join(':')}</span>
                                </div>
                            );
                        }
                    }
                    
                    // Regular paragraph
                    return (
                        <div key={index} className="content-paragraph">
                            {trimmed}
                        </div>
                    );
                })}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="chat-overlay">
            <div 
                className="chat-overlay-backdrop" 
                onClick={(e) => {
                    // Only close if clicking the backdrop itself, not child elements
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }} 
            />
            <div className="chat-overlay-container">
                {/* Header */}
                <div className="chat-overlay-header">
                    <div className="chat-overlay-title">
                        {/* Removed branding */}
                    </div>
                    <button className="chat-overlay-close" onClick={onClose}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                {/* Chat Content */}
                <div className="chat-overlay-content">
                    <div className="chat-section-full">
                        {messages.length > 0 && (
                            <div className="chat-header">
                                <h3>AI Conversation</h3>
                                <button 
                                    onClick={clearChat} 
                                    className="clear-chat-btn"
                                    title="Clear conversation"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z"/>
                                    </svg>
                                </button>
                            </div>
                        )}
                        
                        <div className="messages-container">
                            {messages.length === 0 && (
                                <div className="welcome-message">
                                    <div className="paradox-welcome">
                                        <img src="/src/assets/images/PARADOX-transparant-white.svg" alt="Paradox" className="paradox-logo-large" />
                                        <h4>Paradox Super Intelligence</h4>
                                        <p>Investment Arbitrage AI Research Agent</p>
                                    </div>
                                </div>
                            )}

                            {messages.map((message) => (
                                <div key={message.id} className={`message ${message.role}`}>
                                    <div className="message-content">
                                        <div className="message-text">
                                            {message.role === 'assistant' ? 
                                                renderStructuredContent(message.content) : 
                                                message.content
                                            }
                                        </div>
                                        <div className="message-time">
                                            {formatTimestamp(message.timestamp)}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {isLoading && (
                                <div className="message assistant">
                                    <div className="message-content">
                                        <div className="paradox-loading">
                                            <img src="/src/assets/images/PARADOX-transparant-white.svg" alt="Loading" className="loading-logo rotating" />
                                            <div className="loading-text cursor-pulse">
                                                {showAdvancedLoading 
                                                    ? advancedLoadingMessages[loadingMessageIndex] 
                                                    : "Loading..."
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>

                        <form onSubmit={handleSubmit} className="chat-input-form">
                            <div className="input-container">
                                <input
                                    type="text"
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    placeholder="Type your message..."
                                    disabled={isLoading}
                                    className="chat-input"
                                    autoComplete="off"
                                />
                                <button
                                    type="submit"
                                    disabled={isLoading || !inputMessage.trim()}
                                    className="send-button paradox-send"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path d="M2 21l21-9L2 3v7l15 2-15 2v7z"/>
                                    </svg>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

ChatOverlay.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ChatOverlay;